/**
 * adsConcepts.ts — how sure we are of an idea (concept), decided by rule rather than by the model's say-so.
 *
 * The handoff's scoring (LinkedIn, 1 to 5), applied to what the idea cites:
 *   5  two independent strong proofs     4  one strong proof     3  medium     2  a gap or news only     1  a hunch worth testing
 * Strong proofs are a long-running competitor ad, a stat with a source, or our own data. The model proposes a score and writes the
 * "Up: … Down: …" reasons; this code then lowers the score when the evidence cited cannot carry it, and says so in the reasons.
 * It never raises a score. Pure, so the CMS and the AI Worker agree.
 */
import { z } from "zod";

export const CONCEPT_EVIDENCE_KINDS = ["competitor_ad", "stat", "own_data", "gap", "news"] as const;
export type ConceptEvidenceKind = (typeof CONCEPT_EVIDENCE_KINDS)[number];
export const conceptEvidenceKindSchema = z.enum(CONCEPT_EVIDENCE_KINDS);
export const CONCEPT_EVIDENCE_LABEL: Record<ConceptEvidenceKind, string> = {
  competitor_ad: "A competitor ad that has run a long time",
  stat: "A statistic with a source",
  own_data: "Our own results",
  gap: "A gap nobody covers",
  news: "Dated market news",
};
const STRONG: readonly ConceptEvidenceKind[] = ["competitor_ad", "stat", "own_data"];

export const CONFIDENCE_LABEL: Record<number, string> = {
  1: "A hunch worth testing",
  2: "A gap or news only",
  3: "Medium evidence",
  4: "One strong proof",
  5: "Two strong proofs",
};

export interface ConceptScoreInput {
  evidence: string;
  evidenceKinds: readonly ConceptEvidenceKind[];
  /** The model's own 1 to 5 score. */
  score: number;
  scoreUp: string;
  scoreDown: string;
}

const tidy = (s: string) => s.replace(/\s+/g, " ").trim().replace(/[.;,\s]+$/u, "");

/** The highest score this evidence can support, and why. */
export function scoreCeiling(i: Pick<ConceptScoreInput, "evidence" | "evidenceKinds">): { max: number; why: string } {
  if (!/\d/.test(i.evidence)) return { max: 1, why: "the evidence has no hard data point (a number, a date or a length of time)" };
  const strong = new Set(i.evidenceKinds.filter((k) => STRONG.includes(k)));
  if (strong.size === 0) return { max: 2, why: "the evidence is a gap or news only" };
  if (strong.size === 1) return { max: 4, why: "only one strong proof is cited" };
  return { max: 5, why: "" };
}

export interface SettledConcept {
  score: number;
  scoreReason: string;
  evidenceType: string;
}

export function settleConcept(i: ConceptScoreInput): SettledConcept {
  const asked = Math.min(5, Math.max(1, Math.round(Number.isFinite(i.score) ? i.score : 1)));
  const ceiling = scoreCeiling(i);
  const score = Math.min(asked, ceiling.max);
  const up = tidy(i.scoreUp) || "none given";
  const down = tidy(i.scoreDown) || "none given";
  const capped = score < asked ? ` Lowered from ${asked} to ${score}: ${ceiling.why}.` : "";
  return { score, scoreReason: `Up: ${up}. Down: ${down}.${capped}`, evidenceType: [...new Set(i.evidenceKinds)].join(",") };
}

/** Only a real web address is kept as the evidence link. */
export function cleanEvidenceLink(s: string | null | undefined): string | null {
  const t = (s ?? "").trim();
  return /^https?:\/\/[^\s]+$/i.test(t) ? t.slice(0, 1000) : null;
}

/** "competitor_ad,stat" → "A competitor ad that has run a long time, a statistic with a source" for display. */
export function evidenceTypeText(stored: string | null): string {
  const kinds = (stored ?? "").split(",").map((s) => s.trim()).filter((s): s is ConceptEvidenceKind => (CONCEPT_EVIDENCE_KINDS as readonly string[]).includes(s));
  return kinds.map((k) => CONCEPT_EVIDENCE_LABEL[k]).join("; ");
}

/** Splits "Up: … Down: …. Lowered from 5 to 4: …" back into its parts for display. Older text without that shape comes back whole as `note`. */
export function splitScoreReason(reason: string): { up: string; down: string; note: string } {
  const m = /^Up:\s*(.*?)\.\s*Down:\s*(.*?)\.(?:\s+(.*))?$/su.exec(reason.trim());
  return m ? { up: m[1] ?? "", down: m[2] ?? "", note: m[3] ?? "" } : { up: "", down: "", note: reason.trim() };
}

/**
 * Raya's one-third rule: small and solo practices are one angle, at most about a third of each week's concepts.
 * How many more small-practice concepts a new batch may hold, given what this week already has.
 */
export function smallPracticeRoom(batch: number, weekTotal: number, weekSmall: number): number {
  return Math.max(0, Math.ceil((weekTotal + batch) / 3) - weekSmall);
}
